import React, { useState, useEffect } from 'react';
import { myAxios } from '../services/Helper';
import './admin.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTrash, faIndianRupeeSign, faPlus, faChevronDown } from '@fortawesome/free-solid-svg-icons';

const ManageSubFunctions = () => {
    const [subFunctions, setSubFunctions] = useState([]);
    const [functions, setFunctions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        id: '',
        name: '',
        description: '',
        price: '',
        functionId: ''
    });

    useEffect(() => {
        fetchSubFunctions();
        fetchFunctions();
    }, []);

    const fetchFunctions = async () => {
        try {
            const response = await myAxios.get('/function');
            setFunctions(response.data);
        } catch (err) {
            setError(err.message);
        }
    };

    const fetchSubFunctions = async () => {
        try {
            const response = await myAxios.get('/subfunction');
            setSubFunctions(response.data.map(func => ({
                id: func.subFunctionID,
                name: func.subFunctionName,
                description: func.subFunctionDec,
                price: func.subFunctionPrice,
                addedDate: new Date().toLocaleDateString(),
                functionId: func.functionID?.functionId
            })));
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddSubFunction = async (e) => {
        e.preventDefault();
        try {
            const subFunctionData = {
                subFunctionName: formData.name,
                subFunctionDec: formData.description,
                subFunctionPrice: parseFloat(formData.price),
                functionid: parseInt(formData.functionId)
            };

            if (formData.id) {
                // Update existing sub-function
                await myAxios.put(`/subfunction/${formData.id}`, subFunctionData);
            } else {
                // Create new sub-function
                await myAxios.post('/subfunction/addSubFunction', subFunctionData);
            }

            setFormData({ id: '', name: '', description: '', price: '', functionId: '' });
            setShowForm(false);
            fetchSubFunctions();
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        }
    };

    const handleDeleteSubFunction = async (subFunctionId) => {
        if (window.confirm('Are you sure you want to delete this sub-function?')) {
            try {
                await myAxios.delete(`/subfunction/${subFunctionId}`);
                fetchSubFunctions();
            } catch (err) {
                setError(err.message);
            }
        }
    };

    const handleEditSubFunction = async (subFunctionId) => {
        const subFunction = subFunctions.find(sf => sf.id === subFunctionId);
        if (!subFunction) {
            setError('Sub-function not found');
            return;
        }
        setFormData({
            id: subFunctionId,
            name: subFunction.name,
            description: subFunction.description,
            price: subFunction.price.toString(),
            functionId: subFunction.functionId
        });
        setShowForm(true);
    };

    if (loading) return <div className="loading">Loading sub-functions...</div>;
    if (error) return <div className="error">Error: {error}</div>;

    return (
        <div className="functions-container">
            <div className="functions-header">
                <h2>Sub-Functions Management</h2>
                <button className="add-function-btn" onClick={() => setShowForm(true)}>
                    <FontAwesomeIcon icon={faPlus} /> Add Sub-Function
                </button>
            </div>

            {showForm && (
                <div className="form-overlay">
                    <div className="form-container">
                        <h3>{formData.id ? 'Edit' : 'Add New'} Sub-Function</h3>
                        <form onSubmit={handleAddSubFunction}>
                            <select
                                name="functionId"
                                value={formData.functionId}
                                onChange={handleInputChange}
                                required
                                className="form-select"
                            >
                                <option value="">Select Function</option>
                                {functions.map(func => (
                                    <option key={func.functionId} value={func.functionId}>
                                        {func.functionName}
                                    </option>
                                ))}
                            </select>
                            <input
                                type="text"
                                name="name"
                                placeholder="Sub-Function Name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                                className="form-input"
                            />
                            <textarea
                                name="description"
                                placeholder="Sub-Function Description"
                                value={formData.description}
                                onChange={handleInputChange}
                                required
                                className="form-textarea"
                            ></textarea>
                            <input
                                type="number"
                                name="price"
                                placeholder="Price"
                                value={formData.price}
                                onChange={handleInputChange}
                                required
                                className="form-input"
                            />
                            <div className="form-buttons">
                                <button type="submit" className="btn-primary">
                                    {formData.id ? 'Update' : 'Add'} Sub-Function
                                </button>
                                <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="functions-grid">
                {subFunctions.length === 0 ? (
                    <div className="empty-state">
                        No sub-functions found. Add one to get started.
                    </div>
                ) : (
                    subFunctions.map(func => (
                        <div key={func.id} className="function-card">
                            <div className="function-header">
                                <h3>{func.name}</h3>
                                <span className="date-added">Added: {func.addedDate}</span>
                            </div>
                            <p className="function-description">{func.description}</p>
                            <div className="function-price">
                                <FontAwesomeIcon icon={faIndianRupeeSign} /> {func.price.toFixed(2)}
                            </div>
                            <div className="function-actions">
                                <button
                                    className="action-btn edit-btn"
                                    title="Edit"
                                    onClick={() => handleEditSubFunction(func.id)}
                                >
                                    <FontAwesomeIcon icon={faPencil} />
                                </button>
                                <button
                                    className="action-btn delete-btn"
                                    title="Delete"
                                    onClick={() => handleDeleteSubFunction(func.id)}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                                <button
                                    className="action-btn price-btn"
                                    title="Price details"
                                >
                                    <FontAwesomeIcon icon={faIndianRupeeSign} />
                                </button>
                                <button
                                    className="action-btn add-item-btn"
                                    title="Add Item"
                                    onClick={() => window.location.href = `/admin/events/items`}
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default ManageSubFunctions;